/*
 * One geometry, and the height is derived rather than declared.
 *
 * 12px of type on 12px of leading, in 4px of vertical padding, is 20px. That sum is the whole spec:
 * nothing here sets a height, so nothing inside the badge can be at odds with one. The chips this
 * component replaced did set one (`height: 20px`) and then put a 16px icon into the 12px content box
 * it left, so the icon spilled out of its own fill and the "URL" chip read as taller than the
 * "Journal article" chip beside it (PLT-6260, Janne's review).
 *
 * The sum landing on the same 20px those chips declared is a coincidence worth not misreading: a
 * derived 20px grows to hold a child the author did not anticipate, and a declared one clips it.
 * That difference is the whole bug above, so do not "simplify" this into `height: 20px`.
 *
 * `padding: 4px 8px` and the 4px radius are `getStyleGuide()`'s badge values, which the shipped
 * chips did not use: they carried `3px 6px` and a 3px radius, both off the token scale in a place
 * where the scale has a step. 8px inline is also the answer to "a bit too little margin left and
 * right of URL" in the same review.
 */
.root {
  display: inline-flex;
  align-items: center;
  gap: var(--space__xs);
  padding-block: var(--space__xs);
  padding-inline: var(--space__sm);
  border-radius: var(--border-radius__sm);
  font-size: var(--font-size__sm);
  font-weight: var(--font-weight__medium);
  /* One short line by definition, so it takes leading of exactly its own type size. Anything looser
     pads the box a second time, on an axis the padding above already owns. */
  line-height: var(--line-height__3xs);
  /* A badge labels the thing beside it, and a label that breaks across two lines stops reading as
     one chip. Both call sites sit in a running line of metadata, which is where this bites. */
  white-space: nowrap;
  /* The article metadata line these first shipped in is italic, and a badge is not a continuation
     of the sentence around it. Both hand-rolled chips set this; dropping it would be a regression. */
  font-style: normal;
}

/*
 * An icon component rendered directly inside a badge is the size of the text beside it, whatever size
 * it was asked for.
 *
 * This is the rule that makes the derived height above hold. Icon components render their size as
 * `width`/`height` attributes on the `<svg>`, which are presentation hints, so a stylesheet beats
 * them: `<ArrowUpRightIcon size={16} />` inside a badge is 12px. That covers every icon set in the
 * repo — octicons, Font Awesome and the Streamline icons all render a real `<svg>` and size it
 * through attributes.
 *
 * What it does NOT beat, so do not read it as a blanket guarantee: an inline `style` on the icon, an
 * `<img>` or a font glyph (neither is an `<svg>`), or a child that sets its own `line-height` — a
 * `<Text>` at `height="sm"` inside a badge makes it 30px. Keep a badge's children to a label and an
 * icon and none of those arise.
 *
 * The child selector is deliberate: `.root svg` at (0,1,1) also beat `Buttons.module.css`'s `.svg`
 * (0,1,0), so any component with an internal icon placed inside a badge had its icon silently forced
 * to 12px. `> svg` reaches the icon beside the label and stops there.
 *
 * `flex: none` so the icon keeps its box when the label is long, rather than being squeezed while
 * the text takes the room.
 */
.root > svg {
  inline-size: var(--font-size__sm);
  block-size: var(--font-size__sm);
  flex: none;
}

/*
 * Tones. Each is a fill and an ink, and there is nothing else to vary: a call site picks one of
 * these or it is not a badge.
 *
 * No tone carries a border, and that is a decision rather than an omission (PLT-6260, Janne's second
 * review: "the border around badges feels like extra noise"). The first cut of this component gave
 * every tone a hairline, on `getStyleGuide()`'s note that a pale tint on a white page needs one to
 * stay separated from the canvas. Shipped, it read as noise for a reason the note does not cover:
 * `link`'s hairline is `blue-100`, the same colour as its own fill, so of the two chips that sit
 * side by side in an article row only the neutral one grew a visible outline — and it had never had
 * one before, because the chip it replaced asked for `--platform-gray-200`, a variable defined
 * nowhere, so its whole `border` shorthand was invalid at computed-value time and every longhand in
 * it fell back to its initial value, `border-style: none` included. Fixing that token is what made
 * the border appear. Nobody had asked for it, and the fill alone carries the pill.
 *
 * This is the second place where the in-product badge and `getStyleGuide()`'s badge now differ — the
 * type size is the other — and design/50-components.md §Badge records both as the design owner's
 * call, not as drift to reconcile quietly.
 */
.tone-neutral {
  background-color: var(--color__gray-100);
  color: var(--color__gray-700);
}

/* The categorical badge of design/10-foundations.md: lilac-800 on lilac-100, the pair it shares with
   the active nav row. Shape and place are what tell the two apart, so a badge must stay a small
   inline pill beside content. */
.tone-categorical {
  background-color: var(--color__lilac-100);
  color: var(--color__lilac-800);
}

/*
 * The one tone that is inherited rather than decided, and the reason is worth reading before reusing
 * it: blue's only sanctioned job is the selected segment of a pill tab switcher, and
 * design/10-foundations.md says outright that a badge in blue reads as the wrong kind of thing. This
 * exists because the shipped "URL" chip is blue and PLT-6260 was asked to align its size, not to
 * recolour it. Raised with the design owner; see design/50-components.md §Badge.
 */
.tone-link {
  background-color: var(--color__blue-100);
  color: var(--color__blue-800);
}
